import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bell, BellRing, Smartphone, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useKeyboardShellMounted } from "@/hooks/useVisualViewport";
import {
  ONESIGNAL_APP_ID, PUSH_INVITE_EVENT, enablePush, ensureIdentity, permissionState,
  pushSupport, readSubscription, takePushInvite,
} from "@/lib/push";

const STORAGE_KEY = "fd_push_optin_state"; // "asked" | "subscribed" | "dismissed"
const STATE_VERSION_KEY = "fd_push_optin_v";
/**
 * Bumped when a stored decision stops meaning what it said.
 *
 * v2, 2026-08-20. Before commit 318c480 the card closed and wrote "asked" on
 * every failure — including the one where OneSignal was configured for the
 * wrong origin and `init()` threw on every attempt. Those values record a
 * browser that was never actually asked anything: a UI accident, not a choice.
 * They are discarded once, here, and the invitation comes back. A decision
 * taken after the fix carries this version and is respected for good.
 */
const STATE_VERSION = "2";

function readState() {
  try {
    if (localStorage.getItem(STATE_VERSION_KEY) !== STATE_VERSION) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.setItem(STATE_VERSION_KEY, STATE_VERSION);
      return null;
    }
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // private mode, storage disabled — offer rather than hide
  }
}

function writeState(value) {
  try {
    localStorage.setItem(STORAGE_KEY, value);
    localStorage.setItem(STATE_VERSION_KEY, STATE_VERSION);
  } catch { /* nothing to do; the card will simply offer again */ }
}

/**
 * The invitation to turn push on, offered once per browser.
 *
 * **Why it never appeared on a phone.** The mount effect began with
 * `if (!("Notification" in window)) return;`, and on iOS that constructor does
 * not exist outside a home-screen launch — so an iPhone got nothing, in Safari
 * and, until the PWA was opened from its own icon, in the installed app too.
 * The honest answer on that platform is not silence: it is the one step that
 * makes push possible. `pushSupport()` now separates "cannot" from "not yet",
 * and this renders the second case as its own card.
 *
 * **And why it could be invisible where it did render.** The card sits in the
 * bottom-right corner; DanyFab sits in the same corner at `zIndex: 45`, 56px
 * tall, and the card was at `z-40`. On a desktop the card is wide enough that
 * the FAB overlapped a corner of it. On a phone the FAB lands on the buttons.
 * The card now sits above the FAB, clears its height, and respects the
 * home-indicator inset.
 *
 * **A failed registration says nothing at all.** This card is unsolicited: it
 * appears because we decided to ask, not because anyone clicked. When the ask
 * fails, whatever went wrong belongs to us — the origin OneSignal was
 * configured for, a blocked CDN, a permission the browser refused — and none
 * of it is the reader's to repair. The card was showing them the SDK's own
 * sentence, `Can only be used on: https://www.fixydany.com`, which reads as a
 * broken site to the only person who cannot do anything about it. It now
 * disappears instead, and the detail goes to `console.warn` where the person
 * who can act on it is looking. The one message that survives is the iOS
 * install step, because that is not a failure: it is the single action that
 * makes push possible on that platform.
 *
 * The SDK itself lives in `lib/push.js`, shared with the dashboard toggle:
 * `OneSignal.init()` may run once per page, so two components each holding
 * their own loader is a race rather than a duplication.
 */
export default function PushOptIn() {
  const { t } = useTranslation();
  const { user, loading } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  // The only card that is not the plain invitation: iOS outside a home-screen
  // launch, where the actionable thing is "install it first".
  const [iosInstall, setIosInstall] = useState(false);
  // The moment this invitation is answering, when it was armed by onboarding or
  // by a first job. Changes the sentence, nothing else.
  const [reason, setReason] = useState(null);
  // Which arming the decision below last answered. -1 is "never decided";
  // the decision runs once when the user is first known, and once more for
  // every moment armed after that (T20-M2) — see PUSH_INVITE_EVENT.
  const decidedFor = useRef(-1);
  const [armedCount, setArmedCount] = useState(0);
  useEffect(() => {
    const onArmed = () => setArmedCount((n) => n + 1);
    window.addEventListener(PUSH_INVITE_EVENT, onArmed);
    return () => window.removeEventListener(PUSH_INVITE_EVENT, onArmed);
  }, []);
  // T20-M2. Fixed to the bottom at z-46, above a keyboard shell's 40, so in a
  // conversation this card sat on the composer. It waits instead: `show` is
  // kept, so the invitation is still there on the next screen that is not one.
  const shellMounted = useKeyboardShellMounted();

  /**
   * Re-link this device, on its own effect and before anything else decides.
   *
   * Separate from the invitation deliberately. The effect below returns early
   * for every browser that already subscribed — `state === "subscribed"`, the
   * very first check — and those are exactly the browsers that need this: a
   * subscription made before `login()` shipped is live, invisible to every tag
   * filter, and would never be looked at again by a component whose only job is
   * to ask people who have not subscribed yet.
   *
   * Costs nothing on a device that is already bound: `ensureIdentity` reads a
   * local mark and stops.
   */
  useEffect(() => {
    if (loading || !user?.user_id) return;
    ensureIdentity(user.user_id);
  }, [user, loading]);

  useEffect(() => {
    if (loading || !user) return;
    if (decidedFor.current === armedCount) return;
    decidedFor.current = armedCount;
    if (!ONESIGNAL_APP_ID) return;

    // Consumed before the stored state is consulted: a reason is spent whether
    // or not it ends up shown, which is what stops it waiting for a later page
    // and arriving out of context.
    const armed = takePushInvite();
    if (armed) setReason(armed);

    const state = readState();
    // An armed reason speaks past a generic dismissal — someone who closed the
    // plain invitation weeks ago has still never been told the thing that is
    // actually about them. It never speaks past a subscription, because there
    // is nothing left to ask.
    if (state === "subscribed") return;
    if (state === "dismissed" && !armed) return;

    const support = pushSupport();
    if (support === "unsupported") return;
    // "unconfigured" covers a missing app id and a non-canonical origin alike:
    // both mean the ask would fail, and an ask that cannot succeed is not made.
    if (support === "unconfigured") return;
    if (support === "ios-needs-install") {
      // Not a failure and not a nudge to swallow: it is the only actionable
      // thing this platform can be told, and its absence is why an iPhone saw
      // nothing at all.
      setIosInstall(true);
      const iosTimer = setTimeout(() => setShow(true), 1500);
      return () => clearTimeout(iosTimer);
    }

    const permission = permissionState();
    if (permission === "granted") {
      // Already granted — re-attach this browser quietly. The subscription id
      // can be gone while the permission is not (cleared data, a discarded
      // service worker), so this is a reconnection, not bookkeeping.
      readSubscription().then(({ state: live }) => {
        if (live === "on") writeState("subscribed");
        else enablePush({ externalId: user?.user_id }).catch((e) =>
          console.warn("[FixyDany push] silent re-subscribe failed:", e));
      });
      return;
    }
    if (permission === "denied") {
      // Nothing to show. The browser blocked it, no button here can undo that,
      // and the person who blocked it does not need a card saying so.
      console.warn("[FixyDany push] notifications are blocked for this origin");
      return;
    }

    const timer = setTimeout(() => setShow(true), 1500);
    return () => clearTimeout(timer);
  }, [user, loading, armedCount]);

  if (!show || shellMounted) return null;

  const enable = async () => {
    setBusy(true);
    // Recorded before the attempt: a browser that ends up denied has still been
    // asked, and the invitation does not come back for it.
    writeState("asked");
    try {
      const result = await enablePush({ externalId: user?.user_id });
      if (result.state !== "on") {
        // Denied, no dialog, no subscription — all the same answer here: the
        // card goes away and the state goes to the console.
        console.warn("[FixyDany push] enabling notifications did not take:", result.state);
        return setShow(false);
      }
      writeState("subscribed");
      setShow(false);
    } catch (e) {
      console.warn("[FixyDany push] enabling notifications failed:", e);
      setShow(false);
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    writeState("dismissed");
    setShow(false);
  };

  return (
    <div
      className="fixed left-4 sm:left-auto sm:max-w-sm bg-cream border-[1.5px] border-hive rounded-md shadow-deep p-4 animate-fade-in"
      style={{
        // Above DanyFab (zIndex 45) and clear of it: the FAB is 56px tall in
        // this same corner and on a phone it lands exactly on these buttons.
        zIndex: 46,
        right: "clamp(16px, 4vw, 24px)",
        bottom: "calc(clamp(20px, 5vw, 24px) + env(safe-area-inset-bottom, 0px) + 68px)",
      }}
      data-testid="push-optin-card"
      data-variant={iosInstall ? "ios" : reason || "invite"}
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-smoke hover:text-worker"
        aria-label={t("notif.push.dismiss")}
        data-testid="push-optin-dismiss"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="bg-hive rounded-md p-2 shrink-0">
          {iosInstall
            ? <Smartphone size={20} className="text-worker" />
            : <BellRing size={20} className="text-worker" />}
        </div>
        <div>
          <p className="font-display font-bold text-worker text-sm">
            {t("notif.push.title")}
          </p>

          <p className="mt-1 text-xs text-smoke leading-relaxed">
            {iosInstall
              ? t("push.toggle.iosInstall")
              : reason
              ? t(`notif.push.reason.${reason}`)
              : t("notif.push.body")}
          </p>

          <div className="mt-3 flex gap-2">
            {/* On iOS the permission dialog does not exist until the app is
                installed, so there is no button that could do anything. */}
            {!iosInstall && (
              <button
                onClick={enable}
                disabled={busy}
                className="bg-hive hover:bg-hive-deep text-worker font-display font-semibold text-xs px-3 py-2 rounded-sm transition disabled:opacity-50"
                data-testid="push-optin-enable"
              >
                <Bell size={14} className="inline mr-1" />
                {busy ? t("notif.push.enabling") : t("notif.push.enable")}
              </button>
            )}
            <button
              onClick={dismiss}
              className="text-xs text-smoke hover:text-worker font-display font-medium px-2"
              data-testid="push-optin-later"
            >
              {iosInstall ? t("notif.push.close") : t("notif.push.later")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
